import type { Dispatch, FC, SetStateAction } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import CloseRounded from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import type { AxiosResponse } from 'axios';

import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import useOnDemandReportsDownloadContext from 'src/components/dashboard/DashboardPage/components/ButtonReportModal/contexts/onDemandReportsDownloadContext';
import LanguageSelect from 'src/components/dashboard/DashboardPage/components/LanguageSelect';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';
import useReportConfigMaps from 'src/components/reportConfig/hooks/useReportConfigMaps';

import { styles } from './reportModalDialog.styles';

export interface ReportModalDialogProps {
  showModal: boolean;
  closeDialog: () => void;
  setUnavailableReportTypes: Dispatch<SetStateAction<number[]>>;
}

const ReportModalDialog: FC<ReportModalDialogProps> = ({
  showModal,
  closeDialog,
  setUnavailableReportTypes,
}) => {
  const { t } = useTranslation('dashboard');
  const [isPending, setIsPending] = useState(false);
  const { poll } = useDownloadManager();
  const { dashboardConfig } = useDashboardContext();
  const {
    isLoading,
    selectedLanguage,
    setSelectedLanguage,
    onDemandReports,
    setOnDemandReports,
    errorMsg,
  } = useOnDemandReportsDownloadContext();

  const { reportNamesPerReportType, reportConfigsLoading } =
    useReportConfigMaps();

  const availableOnDemandReports = onDemandReports.filter((report) =>
    Object.keys(reportNamesPerReportType)
      .map(Number)
      .includes(report.report_type),
  );

  const handleCheckboxChange = (report_type: number) => {
    setOnDemandReports((prevState) =>
      prevState.map((report) => ({
        ...report,
        checked:
          report.report_type === report_type ? !report.checked : report.checked,
      })),
    );
  };

  const handleDownloadClick = () => {
    const checkedReports = onDemandReports.filter(({ checked }) => checked);
    const checkedReportIds = checkedReports.map(({ id }) => id).join(',');

    setIsPending(true);

    HttpService.post(generatePath(apiPaths.REPORTS.ON_DEMAND_EXPORT, {}), {
      report_ids: checkedReportIds,
      dashboard_id: dashboardConfig?.id,
    })
      .then(({ data }: AxiosResponse<number[]>) => {
        if (Array.isArray(data)) {
          setUnavailableReportTypes(data);
        } else {
          setUnavailableReportTypes([]);
        }
      })
      .finally(() => {
        setIsPending(false);
        poll();
        // ensure modal is not reopened again if already closed after exporting
        closeDialog();
      });
  };

  const isAnyChecked = onDemandReports.some(({ checked }) => checked);

  return (
    <Dialog
      open={showModal}
      onClose={closeDialog}
      PaperProps={{ sx: styles.paper }}
    >
      <DialogTitle component="div">
        <Stack direction="row" sx={{ justifyContent: 'flex-end', px: 4 }}>
          <IconButton sx={styles.closeButton} onClick={closeDialog}>
            <CloseRounded />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2}>
          <Box sx={{ px: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t('ButtonReportModal.ChooseLanguage')}
            </Typography>
            <LanguageSelect
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
            />
          </Box>
          <Box sx={{ px: 4 }}>
            <Typography variant="h6">
              {t('ButtonReportModal.ChooseType')}
            </Typography>
            {isLoading || reportConfigsLoading ? (
              <Stack direction="row" justifyContent="center" py={2.5}>
                <CircularProgress />
              </Stack>
            ) : (
              <FormGroup>
                {availableOnDemandReports && !errorMsg ? (
                  <Stack direction="column" spacing={1} sx={{ ml: -1.125 }}>
                    {availableOnDemandReports.map(
                      ({ report_type, checked }, i) => (
                        <FormControlLabel
                          key={i}
                          sx={styles.label}
                          label={reportNamesPerReportType[report_type]}
                          control={
                            <Checkbox
                              checked={checked}
                              onChange={() => handleCheckboxChange(report_type)}
                            />
                          }
                        />
                      ),
                    )}
                  </Stack>
                ) : (
                  <Box>{errorMsg}</Box>
                )}
              </FormGroup>
            )}
          </Box>
          <Divider sx={styles.divider} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack direction="row" spacing={1.25} sx={{ px: 4, width: '100%' }}>
          <Button
            variant="cta"
            fullWidth
            disabled={isPending || !isAnyChecked}
            onClick={handleDownloadClick}
          >
            {isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('ButtonReportModal.ExportReports')
            )}
          </Button>
          <Button
            onClick={closeDialog}
            variant="cta"
            color="secondary"
            fullWidth
          >
            {t('ButtonReportModal.Cancel')}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ReportModalDialog;
